import { useEffect } from 'react'
import { fetchFileList } from '../features/Filesystem/api/fileApi'
import { deriveFilesUrl } from '../features/Filesystem/utils/filesHelper'
import { type FileInfo } from '../utils/types'
import LogTool from '../logger/logTools'

interface UseFetchExistingFilesParams {
  open: boolean
  formState: { state: any }
  openFilesDrawer: boolean
  setUploadedFiles: (files: FileInfo[]) => void
  logContext: string
  resetOnClose?: boolean
}

/**
 * Custom Hook to fetch existing files when drawer opens, formState changes, or FilesDrawer opens.
 * Automatically manages the uploadedFiles state.
 */
export default function useFetchExistingFiles({
  open,
  formState,
  openFilesDrawer,
  setUploadedFiles,
  logContext,
  resetOnClose = true,
}: UseFetchExistingFilesParams): void {
  const log = new LogTool({ context: `${logContext.charAt(0).toUpperCase() + logContext.slice(1)}Drawer`, enable: true, logLevel: 'debug' })

  useEffect(() => {
    if (!formState.state) {
      if (resetOnClose) {
        setUploadedFiles([])
      }
      return
    }

    if (!open && !openFilesDrawer) {
      if (resetOnClose) {
        setUploadedFiles([])
      }
      return
    }

    const fetchExistingFiles = async (): Promise<void> => {
      try {
        const filesUrl = deriveFilesUrl(formState.state)
        if (filesUrl) {
          const fileResponse = await fetchFileList(filesUrl)
          // Store already uploaded files separately from new files to upload
          setUploadedFiles(fileResponse.results)
        } else {
          setUploadedFiles([])
        }
      } catch (error) {
        log.error(`Error fetching ${logContext} files`, error)
        setUploadedFiles([])
      }
    }

    void fetchExistingFiles()
  }, [open, openFilesDrawer, formState.state?.self, logContext, resetOnClose, setUploadedFiles])
}
